<script setup lang="ts">
import { computed, onBeforeMount, ref, watch } from 'vue'
import { showPocPage } from '@/composables/feature-flag'
import { T } from '@tolgee/vue'
import { useHapticFeedback } from '@/composables/game/haptic-feedback'
import TabButton from '@/components/TabButton.vue'
import router from '@/router'
import { RouteName } from '@/routes'
import { useFortuneWheel, useGoldWheel } from '@/composables/game/fortune-wheel'
import { formatNumberFullTruncate } from '@/helpers/utils'
import SpinTab from '@/components/SpinTab.vue'
import { twMerge } from 'tailwind-merge'
import MoneyEyeEmoji from '@/assets/images/gifs/emoji-money-eye.gif'
import ExplodeEmojisAnimation from '@/components/ExplodeEmojisAnimation.vue'
import AirdropTab from '@/components/AirdropTab.vue'
import { useRoute } from 'vue-router'
import { Vue3Lottie } from 'vue3-lottie'
import IWDBackgroundJson from '@/assets/lotties/periodic/IWD/IWD-background.json'
import STPBackgroundJson from '@/assets/lotties/periodic/STP/STP-background.json'
import { useShop } from '@/composables/game/shop'
import { useFullScreenDimensions } from '@/composables/helpers/fullscreen'

enum RewardTab {
	SPIN = 'spin',
	EXTRA = 'extra'
}

const { topNumber } = useFullScreenDimensions()

const t = ref<ReturnType<typeof setTimeout>>()

const { addHapticFeedback } = useHapticFeedback()
const { currentBalance, updateState } = useFortuneWheel()
const { isGold: showGold } = useGoldWheel()
const route = useRoute()
const { isIWD, isSTP } = useShop()

const isGold = ref<boolean>(showGold.value)
const activeTab = ref<RewardTab>(RewardTab.SPIN)
const balanceToShow = ref<number>(currentBalance.value)
const initFinished = ref<boolean>(false)
const isGoldTab = computed<boolean>(() => isGold.value && activeTab.value === RewardTab.SPIN)
const isLoadingAd = ref(false)

const showBalanceAnimation = ref(false)

function onPrizeClaimed() {
  showBalanceAnimation.value = true
  setTimeout(() => showBalanceAnimation.value = false, 2000)
  updateBalance()
}

function press(e: Event) {
  e.preventDefault()
  t.value = setTimeout(() => {
    showPocPage.value = true
    addHapticFeedback('heavy')
  }, 3_000)
}

function release() {
  clearTimeout(t.value)
}

function updateBalance() {
  balanceToShow.value = currentBalance.value
}

onBeforeMount(async () => {
  if (route.query.tab) {
    activeTab.value = RewardTab.EXTRA
  }
  await updateState()
  initFinished.value = true
})

watch(initFinished, () => {
  if (initFinished.value) {
    updateBalance()
  }
})
</script>

<template>
  <div
    v-if="isIWD"
    class="fixed top-0 left-0 w-screen h-screen pointer-events-none flex items-center justify-center"
  >
    <Vue3Lottie
      :animation-data="IWDBackgroundJson"
      :auto-play="true"
      :loop="true"
      width="auto"
      height="100%"
      class="min-w-full"
    />
  </div>
  <div
    v-else-if="isSTP"
    class="fixed left-[-200px] w-[calc(100dvw+400px)] h-[calc(100dvh+600px)] pointer-events-none flex items-center justify-center"
    :style="{top: (-topNumber - 70) + 'px'}"
  >
    <Vue3Lottie
      :animation-data="STPBackgroundJson"
      :auto-play="true"
      :loop="true"
      width="100%"
      height="auto"
      class="translate-x-[50px]"
    />
  </div>
  <div
    :class="twMerge('flex flex-col items-center pt-6 px-4 overflow-hidden relative flex-1')"
  >
    <div
      class="flex w-full relative justify-center mb-6 z-[3]"
    >
      <h1
        class="text-light-white text-[32px] leading-[32px] font-bold uppercase bounce-in-right "
        v-touch:press="press"
        v-touch:release="release"
      >
        <T
          key-name="rewards.title"
          default-value="Rewards"
        />
      </h1>

      <div
        class="py-1.5 pr-3 pl-2 flex gap-x-1.5 bg-dark-top-layer rounded-[41px]  absolute right-0 top-1/2 -translate-y-1/2 items-center"
        :class="{'pointer-events-none': isLoadingAd}"
        @click="router.push({name: RouteName.BALANCE})"
      >
        <span class="relative">
          <img
            src="@/assets/images/balance-currency.png"
            class="object-contain size-4"
          >
          <ExplodeEmojisAnimation
            :show="showBalanceAnimation"
            :image-src="MoneyEyeEmoji"
            image-rotate-angle="200deg"
          />
        </span>
        <div class="text-sm font-semibold text-light-white">
          {{ `$${formatNumberFullTruncate(balanceToShow, true)}` }}
        </div>
      </div>
    </div>

    <div class="z-[1] relative w-full">
      <div
        :class="twMerge('flex w-full rounded-xl p-0.5 bg-dark-top-layer z-[3] relative',
                        activeTab === RewardTab.SPIN ? 'mb-4' : 'mb-0',
                        isGoldTab && 'bg-dark-top-layer/[24%] z-[3]')"
      >
        <TabButton
          :is-active="activeTab === RewardTab.SPIN"
          :is-gold="isGoldTab"
          @click="addHapticFeedback(); activeTab = RewardTab.SPIN"
        >
          <T
            key-name="rewards.spin"
            default-value="Spin"
          />
        </TabButton>
        <TabButton
          :is-active="activeTab === RewardTab.EXTRA"
          :is-gold="isGoldTab"
          @click="addHapticFeedback(); activeTab = RewardTab.EXTRA"
        >
          <div class="flex items-center gap-1.5 gold-text">
            <T
              key-name="rewards.dropee"
              default-value="$DROPEE"
            />
          </div>
        </TabButton>
      </div>
    </div>
    <div
      v-if="activeTab === RewardTab.SPIN"
      class="w-full flex-1 flex flex-col"
    >
      <SpinTab
        :init-finished="initFinished"
        @on-prize-claimed="onPrizeClaimed"
        @handle-is-gold="(value: boolean) => isGold = value"
        @is-loading-ad="isLoadingAd = $event"
      />
    </div>
    <div
      v-else-if="activeTab === RewardTab.EXTRA"
      class="flex flex-col items-center justify-center w-full overflow-visible h-full flex-1"
    >
      <AirdropTab />
    </div>
  </div>
</template>
<style scoped lang="scss">
.gold-text {
	background: linear-gradient(
			to right,
			#fff246 0%,
			#e5b80b 37%,
			#fff571 69%,
			#e5b80b 100%
	);
	background-clip: text;
	-webkit-background-clip: text;
	-webkit-text-fill-color: transparent;
}

</style>
